// src/components/maintenance/tabs/MTTRTab.js
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Alert,
  Spinner,
  Button,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExcel,
  faSyncAlt,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import MaintenanceContext from "../../../contexts/MaintenanceContext";
import axios from "axios";
import ExcelJS from "exceljs";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";
import { popup } from "../../common/popup";

const MTTRTab = () => {
  // Contexts
  const { setError: setContextError } = useContext(MaintenanceContext);

  // States
  const [viewType, setViewType] = useState("global");
  const [startDate, setStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .split("T")[0],
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [mttrData, setMttrData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [error, setError] = useState("");
  const [sectors, setSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState("");

  // Refs
  const chartRef = useRef(null);
  const reportRef = useRef(null);

  // Constants
  const months = [
    "Januari",
    "Februari",
    "Maret",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Agustus",
    "September",
    "Oktober",
    "November",
    "Desember",
  ];

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i,
  );

  // API Helpers
  const getConfig = () => ({
    headers: { "x-auth-token": localStorage.getItem("token") },
  });

  // Fetch sectors
  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const res = await axios.get("/api/sectors", getConfig());
        setSectors(res.data);
        if (res.data.length > 0) setSelectedSector(res.data[0].id);
      } catch (err) {
        console.error("Error mengambil data sektor:", err);
        setError("Gagal mengambil data sektor");
      }
    };

    fetchSectors();
  }, []);

  // Fetch MTTR data
  useEffect(() => {
    const fetchMTTRData = async () => {
      if (viewType === "sector" && !selectedSector) return;

      try {
        setLoading(true);
        setError("");
        if (setContextError) setContextError("");

        // Fetch MTTR summary data
        let url = `/api/maintenance/mttr?month=${selectedMonth}&year=${selectedYear}&type=${viewType}`;
        if (viewType === "sector") url += `&sectorId=${selectedSector}`;

        const res = await axios.get(url, getConfig());

        const receivedData = res.data || {
          mttr: 0,
          totalRepairTime: 0,
          repairCount: 0,
          sectorName: viewType === "global" ? "Global" : "Unknown Sector",
        };

        setMttrData(receivedData);

        // Fetch maintenance history for daily data
        await fetchDailyMTTRData();
      } catch (err) {
        console.error("Error mengambil data MTTR:", err);
        setError("Gagal mengambil data MTTR");
        if (setContextError) setContextError("Gagal mengambil data MTTR");
      } finally {
        setLoading(false);
      }
    };

    fetchMTTRData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, viewType, selectedSector, setContextError]);

  // Fetch daily MTTR data
  const fetchDailyMTTRData = async () => {
    try {
      // Calculate date range for the selected month
      const startDateOfMonth = new Date(selectedYear, selectedMonth, 1);
      const endDateOfMonth = new Date(selectedYear, selectedMonth + 1, 0);

      const formattedStartDate = startDateOfMonth.toISOString().split("T")[0];
      const formattedEndDate = endDateOfMonth.toISOString().split("T")[0];

      // Get maintenance history
      let url = `/api/maintenance/history?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
      if (viewType === "sector" && selectedSector)
        url += `&sector=${selectedSector}`;

      const res = await axios.get(url, getConfig());

      // Initialize daily data array
      const daysInMonth = new Date(
        selectedYear,
        parseInt(selectedMonth) + 1,
        0,
      ).getDate();
      const initialDailyData = Array.from({ length: daysInMonth }, (_, i) => ({
        day: i + 1,
        mttr: 0,
        totalTime: 0,
        count: 0,
        date: i + 1 < 10 ? `0${i + 1}` : `${i + 1}`,
      }));

      // Group maintenance records by day
      const maintenanceByDay = {};

      res.data.forEach((record) => {
        const endDate = new Date(record.endTime);
        const day = endDate.getDate();

        // Calculate repair time in minutes
        const startMs = new Date(
          record.maintenanceStartTime || record.startTime,
        ).getTime();
        const endMs = endDate.getTime();
        const repairTimeMinutes = Math.round((endMs - startMs) / 60000);

        if (!maintenanceByDay[day]) {
          maintenanceByDay[day] = { totalTime: 0, count: 0 };
        }

        maintenanceByDay[day].totalTime += repairTimeMinutes;
        maintenanceByDay[day].count += 1;
      });

      // Update daily data with maintenance info
      Object.keys(maintenanceByDay).forEach((day) => {
        const dayIndex = parseInt(day) - 1;
        if (dayIndex >= 0 && dayIndex < initialDailyData.length) {
          const dayData = maintenanceByDay[day];
          initialDailyData[dayIndex].totalTime = dayData.totalTime;
          initialDailyData[dayIndex].count = dayData.count;
          initialDailyData[dayIndex].mttr =
            dayData.count > 0
              ? parseFloat((dayData.totalTime / dayData.count).toFixed(1))
              : 0;
        }
      });

      setChartData(initialDailyData);
    } catch (err) {
      console.error("Error mengambil data MTTR harian:", err);
    }
  };

  // Handle Excel export
  const handleExportToExcel = async () => {
    try {
      if (!mttrData || mttrData.repairCount === 0) {
        popup.alert("Tidak ada data untuk diekspor");
        return;
      }

      setLoading(true);

      // Get screenshots
      if (!chartRef.current)
        throw new Error("Referensi grafik tidak ditemukan");

      const canvas = await html2canvas(chartRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL("image/png");

      let summaryImgData = null;
      if (reportRef.current) {
        const summaryCanvas = await html2canvas(reportRef.current, {
          scale: 2,
          useCORS: true,
          logging: false,
        });
        summaryImgData = summaryCanvas.toDataURL("image/png");
      }

      // Create Excel workbook
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("MTTR Report");

      // Set column widths
      worksheet.columns = [
        { header: "Tanggal", key: "date", width: 15 },
        { header: "MTTR (menit)", key: "mttr", width: 15 },
        { header: "Total Waktu (menit)", key: "totalTime", width: 20 },
        { header: "Jumlah Maintenance", key: "count", width: 20 },
      ];

      // Add title
      worksheet.mergeCells("A1:D1");
      const titleCell = worksheet.getCell("A1");
      titleCell.value = "LAPORAN MTTR (MEAN TIME TO REPAIR)";
      titleCell.font = { bold: true, size: 16 };
      titleCell.alignment = { horizontal: "center" };

      // Add MTTR info
      worksheet.getCell("A3").value = "Periode:";
      worksheet.getCell("B3").value =
        `${months[selectedMonth]} ${selectedYear}`;

      worksheet.getCell("A4").value = "Sektor:";
      worksheet.getCell("B4").value = mttrData.sectorName || "Global";

      worksheet.getCell("A5").value = "MTTR:";
      worksheet.getCell("B5").value = `${mttrData.mttr} menit`;

      worksheet.getCell("A6").value = "Total Waktu Perbaikan:";
      worksheet.getCell("B6").value = `${mttrData.totalRepairTime} menit`;

      worksheet.getCell("A7").value = "Jumlah Kerusakan:";
      worksheet.getCell("B7").value = `${mttrData.repairCount} kali`;

      // Add header styles
      ["A3", "A4", "A5", "A6", "A7"].forEach((cell) => {
        worksheet.getCell(cell).font = { bold: true };
      });

      // Add table headers
      worksheet.getCell("A9").value = "Tanggal";
      worksheet.getCell("B9").value = "MTTR (menit)";
      worksheet.getCell("C9").value = "Total Waktu (menit)";
      worksheet.getCell("D9").value = "Jumlah Maintenance";

      ["A9", "B9", "C9", "D9"].forEach((cell) => {
        worksheet.getCell(cell).font = { bold: true };
        worksheet.getCell(cell).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFD3D3D3" },
        };
        worksheet.getCell(cell).border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Add data rows
      const filteredData = chartData.filter((item) => item.mttr > 0);
      let rowIndex = 10;

      filteredData.forEach((item) => {
        const month = selectedMonth + 1;
        const formattedMonth = month < 10 ? `0${month}` : month;
        const date = `${selectedYear}-${formattedMonth}-${item.date}`;

        worksheet.getCell(`A${rowIndex}`).value = date;
        worksheet.getCell(`B${rowIndex}`).value = parseFloat(
          item.mttr.toFixed(1),
        );
        worksheet.getCell(`C${rowIndex}`).value = item.totalTime || 0;
        worksheet.getCell(`D${rowIndex}`).value = item.count || 0;

        ["A", "B", "C", "D"].forEach((col) => {
          worksheet.getCell(`${col}${rowIndex}`).border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });

        rowIndex++;
      });

      // Add images
      if (summaryImgData) {
        const summaryImageId = workbook.addImage({
          base64: summaryImgData,
          extension: "png",
        });

        worksheet.addImage(summaryImageId, {
          tl: { col: 0, row: rowIndex + 1 },
          ext: { width: 400, height: 120 },
        });

        rowIndex += 10;
      }

      const chartImageId = workbook.addImage({
        base64: imgData,
        extension: "png",
      });

      worksheet.addImage(chartImageId, {
        tl: { col: 0, row: rowIndex + 1 },
        ext: { width: 600, height: 400 },
      });

      // Export to file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `MTTR_Report_${months[selectedMonth]}_${selectedYear}.xlsx`);

      setLoading(false);
    } catch (error) {
      console.error("Error saat mengekspor data:", error);
      popup.error("Gagal mengekspor data: " + error.message);
      setLoading(false);
    }
  };

  // Handle date filter
  const handleFilter = async () => {
    try {
      setError("");
      setLoading(true);

      // Validate dates
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        throw new Error("Tanggal tidak valid");
      }

      if (start > end) {
        throw new Error("Tanggal mulai harus sebelum tanggal akhir");
      }

      // Get maintenance history for date range
      let url = `/api/maintenance/history?startDate=${startDate}&endDate=${endDate}`;
      if (viewType === "sector" && selectedSector) {
        url += `&sector=${selectedSector}`;
      }

      const res = await axios.get(url, getConfig());

      if (res.data.length === 0) {
        // No data in filter range
        setMttrData({
          mttr: 0,
          totalRepairTime: 0,
          repairCount: 0,
          sectorName:
            viewType === "sector"
              ? sectors.find((s) => String(s.id) === String(selectedSector))?.name
              : "Global",
        });

        // Reset chart data
        const daysInMonth = new Date(
          selectedYear,
          parseInt(selectedMonth) + 1,
          0,
        ).getDate();
        const emptyChartData = Array.from({ length: daysInMonth }, (_, i) => ({
          day: i + 1,
          mttr: 0,
          totalTime: 0,
          count: 0,
          date: i + 1 < 10 ? `0${i + 1}` : `${i + 1}`,
        }));

        setChartData(emptyChartData);
      } else {
        // Calculate MTTR from maintenance records
        let totalRepairTime = 0;
        let totalRepairCount = 0;

        // Group by day
        const maintenanceByDay = {};

        res.data.forEach((record) => {
          // Calculate repair time
          const startMs = new Date(
            record.maintenanceStartTime || record.startTime,
          ).getTime();
          const endMs = new Date(record.endTime).getTime();
          const repairTimeMinutes = Math.round((endMs - startMs) / 60000);

          totalRepairTime += repairTimeMinutes;
          totalRepairCount += 1;

          // Group by day
          const day = new Date(record.endTime).getDate();

          if (!maintenanceByDay[day]) {
            maintenanceByDay[day] = { totalTime: 0, count: 0 };
          }

          maintenanceByDay[day].totalTime += repairTimeMinutes;
          maintenanceByDay[day].count += 1;
        });

        // Update MTTR data
        const mttrValue =
          totalRepairCount > 0 ? totalRepairTime / totalRepairCount : 0;
        setMttrData({
          mttr: mttrValue.toFixed(1),
          totalRepairTime: totalRepairTime,
          repairCount: totalRepairCount,
          sectorName:
            viewType === "sector"
              ? sectors.find((s) => String(s.id) === String(selectedSector))?.name
              : "Global",
        });

        // Update chart data
        const daysInMonth = new Date(
          selectedYear,
          parseInt(selectedMonth) + 1,
          0,
        ).getDate();
        const updatedChartData = Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const dayData = maintenanceByDay[day] || { totalTime: 0, count: 0 };
          const mttrValue =
            dayData.count > 0
              ? parseFloat((dayData.totalTime / dayData.count).toFixed(1))
              : 0;

          return {
            day,
            mttr: mttrValue,
            totalTime: dayData.totalTime,
            count: dayData.count,
            date: day < 10 ? `0${day}` : `${day}`,
          };
        });

        setChartData(updatedChartData);
      }
    } catch (err) {
      console.error("Error mengaplikasikan filter:", err);
      setError("Gagal menerapkan filter: " + err.message);
      if (setContextError) setContextError("Gagal menerapkan filter");
    } finally {
      setLoading(false);
    }
  };

  // Render bar chart
  const renderBarChart = () => {
    const hasValueData = chartData.filter((item) => item.mttr > 0);

    if (hasValueData.length === 0) {
      return (
        <div className="text-center p-5">
          <p>Tidak ada data untuk ditampilkan</p>
        </div>
      );
    }

    // Chart config
    const mttrThreshold = 82; // minutes
    const chartHeight = 300;
    const maxMttrValue = Math.max(
      ...chartData.map((item) => item.mttr),
      mttrThreshold,
    );
    const maxYValue = Math.max(100, Math.ceil(maxMttrValue / 10) * 10);

    // Generate Y-axis labels
    const yAxisLabels = Array.from({ length: 5 }, (_, i) =>
      Math.round((maxYValue / 4) * i),
    ).reverse();

    // Helper function to calculate bar height
    const getBarHeight = (mttrValue) => (mttrValue / maxYValue) * chartHeight;

    return (
      <div style={{ padding: "20px 0" }}>
        <h5 className="text-center mb-4">Tren MTTR Harian</h5>

        <div
          style={{
            position: "relative",
            height: `${chartHeight + 60}px`,
            marginBottom: "30px",
          }}
        >
          {/* Y-axis labels */}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              height: chartHeight,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              paddingRight: "10px",
              fontSize: "12px",
            }}
          >
            {yAxisLabels.map((label) => (
              <div key={`y-${label}`}>{label}</div>
            ))}
          </div>

          {/* Threshold line */}
          <div
            style={{
              position: "absolute",
              left: "30px",
              right: 0,
              top: `${chartHeight - getBarHeight(mttrThreshold)}px`,
              height: "2px",
              backgroundColor: "red",
              zIndex: 10,
            }}
          ></div>

          {/* Y-axis grid lines */}
          <div
            style={{
              position: "absolute",
              left: "30px",
              right: 0,
              top: 0,
              height: chartHeight,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            {yAxisLabels.map((_, idx) => (
              <div
                key={`grid-${idx}`}
                style={{
                  width: "100%",
                  height: "1px",
                  backgroundColor: "#eee",
                }}
              />
            ))}

            {yAxisLabels.map((_, idx) => (
              <div
                key={idx}
                style={{
                  width: "100%",
                  height: "1px",
                  backgroundColor: "#eee",
                }}
              ></div>
            ))}
          </div>

          {/* Bars container */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              height: chartHeight,
              width: "100%",
              position: "relative",
              paddingLeft: "30px",
            }}
          >
            {chartData.map((item, index) => (
              <div
                key={index}
                style={{
                  flex: 1,
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  minWidth: 0,
                }}
              >
                {item.mttr > 0 && (
                  <div
                    style={{
                      width: "70%",
                      margin: "0 auto",
                      height: `${getBarHeight(item.mttr)}px`,
                      backgroundColor:
                        item.mttr > mttrThreshold ? "#dc3545" : "#007bff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "10px",
                      borderRadius: "4px 4px 0 0",
                      overflow: "hidden",
                    }}
                  >
                    {item.mttr.toFixed(1)}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* X-axis labels - show every other day to avoid crowding */}
          <div
            style={{
              display: "flex",
              width: "100%",
              marginTop: "5px",
              paddingLeft: "30px",
              overflowX: "hidden",
            }}
          >
            {chartData.map((item, index) => (
              <div
                key={index}
                style={{
                  flex: 1,
                  textAlign: "center",
                  fontSize: "10px",
                  color: "#666",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                {index % 2 === 0 ? item.date : ""}
              </div>
            ))}
          </div>

          {/* X-axis label */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              marginTop: "10px",
              paddingLeft: "30px",
              paddingRight: "10px",
            }}
          >
            <div style={{ fontSize: "14px", fontWeight: "bold" }}>1</div>
            <div
              style={{
                textAlign: "center",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              Tanggal
            </div>
            <div style={{ fontSize: "14px", fontWeight: "bold" }}>
              {chartData.length}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render MTTR view
  const renderMTTRView = () => {
    if (!mttrData || mttrData.repairCount === 0) {
      return (
        <Alert variant="info">
          Tidak ada data perbaikan untuk periode yang dipilih.
        </Alert>
      );
    }

    const title =
      viewType === "global"
        ? "MTTR Global"
        : `MTTR untuk Sektor ${mttrData.sectorName}`;

    return (
      <>
        <Card className="mb-4">
          <Card.Body ref={reportRef}>
            <Card.Title>{title}</Card.Title>
            <div className="text-center my-4">
              <h3>MTTR: {mttrData.mttr} menit</h3>
              <p className="text-muted">
                Total Waktu Perbaikan: {mttrData.totalRepairTime} menit | Jumlah
                Kerusakan: {mttrData.repairCount} kali
              </p>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div ref={chartRef}>{renderBarChart()}</div>
          </Card.Body>
        </Card>
      </>
    );
  };

  // Main render
  return (
    <Container fluid style={{ marginTop: "5px" }}>
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ marginBottom: "10px" }}
      >
        <h3 className="m-0">MTTR (Mean Time To Repair)</h3>
        <div className="d-flex align-items-center">
          <Button
            variant="outline-secondary"
            onClick={() => window.location.reload()}
            className="me-2"
          >
            <FontAwesomeIcon icon={faSyncAlt} />
          </Button>
          <Button variant="primary" onClick={handleFilter} className="me-2">
            <FontAwesomeIcon icon={faSearch} className="me-2" />
            Terapkan Filter
          </Button>
          <Button variant="success" onClick={handleExportToExcel}>
            <FontAwesomeIcon icon={faFileExcel} className="me-2" />
            Ekspor ke Excel
          </Button>
        </div>
      </div>

      <Card className="mb-4">
        <Card.Body style={{ padding: "15px" }}>
          <div className="d-flex justify-content-end mb-2">
            <h5>Filter</h5>
          </div>

          <Row>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Tipe Tampilan</Form.Label>
                <Form.Select
                  value={viewType}
                  onChange={(e) => setViewType(e.target.value)}
                >
                  <option value="global">Global</option>
                  <option value="sector">Berdasarkan Sektor</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Pilih Sektor</Form.Label>
                <Form.Select
                  value={selectedSector}
                  onChange={(e) => setSelectedSector(e.target.value)}
                  disabled={
                    viewType !== "sector" || loading || sectors.length === 0
                  }
                >
                  {sectors.map((sector) => (
                    <option key={sector.id} value={sector.id}>
                      {sector.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Tanggal Mulai</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Tanggal Akhir</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Bulan</Form.Label>
                <Form.Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                >
                  {months.map((month, index) => (
                    <option key={index} value={index}>
                      {month}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Tahun</Form.Label>
                <Form.Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading ? (
        <Card>
          <Card.Body className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Memuat...</span>
            </Spinner>
          </Card.Body>
        </Card>
      ) : (
        renderMTTRView()
      )}
    </Container>
  );
};

export default MTTRTab;
